import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import StatCard from '../../components/ui/StatCard';
import GradientIcon from '../../components/ui/GradientIcon';
import { LayoutGrid, PlayCircle, Calendar, CheckCircle2, Clock, CalendarClock } from 'lucide-react';
import RescheduleSessionModal from '../../components/session/RescheduleSessionModal';
import Pagination from '../../components/ui/Pagination';
import ResponsiveDataTable from '../../components/ui/ResponsiveDataTable';
import { trainerService, sessionService } from '../../services';
import { notifications } from '../../utils/toast';
import { usePreferences } from '../../contexts/PreferencesContext';

/**
 * Trainer Sessions Page
 * Shows sessions assigned to the trainer
 * Solo trainers (individual org) can also create, duplicate, and delete sessions
 */

const TrainerSessionsPage = () => {
  const navigate = useNavigate();
  const { formatDateWithTimezone, formatTime } = usePreferences();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, live, scheduled, completed, expired
  const [stats, setStats] = useState({ total: 0, live: 0, scheduled: 0, completed: 0, expired: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [earlyStartModal, setEarlyStartModal] = useState({ show: false, timeRemaining: '', scheduledTime: '' });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, session: null });
  const [rescheduleModal, setRescheduleModal] = useState({ isOpen: false, session: null });
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Check if current user is a solo trainer (individual org type)
  const isSoloTrainer = () => {
    try {
      const userData = localStorage.getItem('user');
      if (!userData) return false;
      const user = JSON.parse(userData);
      const org = user.primaryOrganization || user.organization;
      return org?.type === 'individual';
    } catch {
      return false;
    }
  };

  const soloTrainer = isSoloTrainer();

  const handleDuplicateSession = async (session, e) => {
    e.stopPropagation();
    try {
      const response = await sessionService.duplicateSession(session._id);
      if (response.success) {
        const newSessionId = response.data?._id || response.data?.id;
        notifications.success(`"${session.name}" duplicated successfully! All subsessions, activities, and workbooks have been copied.`);
        // Same flow as the client panel: open the copy's edit page so the date and
        // time can be changed straight away — a duplicate always needs a new slot.
        // Duplicate is a solo-trainer-only action, so the edit route is reachable.
        if (soloTrainer && newSessionId) {
          navigate(`/trainer/sessions/${newSessionId}/edit`);
        } else {
          fetchSessions();
        }
      } else {
        notifications.error(response.error || 'Failed to duplicate session');
      }
    } catch (error) {
      console.error('Error duplicating session:', error);
      notifications.error(error.message || 'Failed to duplicate session');
    }
  };

  // Click handler — opens the styled confirmation modal. The native
  // window.confirm() it replaced was visually disconnected from the rest
  // of the app and gave admins no chance to cancel after the click landed.
  const handleDeleteSession = (session, e) => {
    e.stopPropagation();
    setDeleteModal({ isOpen: true, session });
  };

  // Actual delete — runs only after the user confirms in the modal.
  const confirmDeleteSession = async () => {
    const session = deleteModal.session;
    if (!session) return;
    try {
      const response = await sessionService.deleteSession(session._id);
      if (response.success) {
        notifications.success(`"${session.name}" deleted successfully.`);
        fetchSessions();
      } else {
        notifications.error(response.error || 'Failed to delete session');
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      notifications.error(error.message || 'Failed to delete session');
    }
  };

  // Helper function to calculate effective status based on time.
  // A live session stays live until the trainer ends it — sessions routinely
  // overrun their planned end time and must not be shown as expired. The backend
  // deliberately never touches a live session, so re-deriving it here would make
  // the list contradict the server.
  // Only a session that was scheduled and never started expires when its window passes.
  const getEffectiveStatus = (session) => {
    if (session.status !== 'scheduled') {
      return session.status;
    }

    const now = new Date();
    const endTime = session.endTime ? new Date(session.endTime) : null;

    if (endTime && !isNaN(endTime.getTime()) && endTime < now) {
      return 'expired';
    }

    return session.status;
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      // Always fetch ALL sessions — filtering is done locally
      const params = { limit: 1000 };
      const response = await trainerService.getSessions(params);
      if (response.success) {
        // Apply effective status to each session
        const sessionsWithEffectiveStatus = (response.data || []).map(session => ({
          ...session,
          status: getEffectiveStatus(session),
        }));
        // Sort by createdAt descending (newest first)
        sessionsWithEffectiveStatus.sort((a, b) => {
          const dateA = new Date(a.createdAt || a.scheduledDate || 0);
          const dateB = new Date(b.createdAt || b.scheduledDate || 0);
          return dateB - dateA;
        });
        setSessions(sessionsWithEffectiveStatus);

        // Calculate stats from ALL sessions
        const recalculatedStats = {
          total: sessionsWithEffectiveStatus.length,
          live: sessionsWithEffectiveStatus.filter(s => s.status === 'live').length,
          scheduled: sessionsWithEffectiveStatus.filter(s => s.status === 'scheduled').length,
          completed: sessionsWithEffectiveStatus.filter(s => s.status === 'completed').length,
          expired: sessionsWithEffectiveStatus.filter(s => s.status === 'expired').length,
        };
        setStats(recalculatedStats);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartSession = async (sessionId) => {
    // Find the session to check its scheduled start time
    const session = sessions.find(s => s._id === sessionId);
    console.log('[TrainerSessions] handleStartSession:', { sessionId, session, sessionsCount: sessions.length });

    if (session) {
      const scheduledStart = session.startTime || session.scheduledStartTime || session.scheduledDate;
      console.log('[TrainerSessions] Scheduled start:', scheduledStart, 'Session fields:', {
        startTime: session.startTime,
        scheduledStartTime: session.scheduledStartTime,
        scheduledDate: session.scheduledDate
      });
      if (scheduledStart) {
        const startTime = new Date(scheduledStart);

        // Times are stored as pseudo-UTC (local time with Z suffix)
        // Create pseudo-UTC "now" by getting current local time components directly
        const now = new Date();
        const localYear = now.getFullYear();
        const localMonth = String(now.getMonth() + 1).padStart(2, '0');
        const localDay = String(now.getDate()).padStart(2, '0');
        const localHours = String(now.getHours()).padStart(2, '0');
        const localMinutes = String(now.getMinutes()).padStart(2, '0');
        const localSeconds = String(now.getSeconds()).padStart(2, '0');
        const pseudoUtcNow = new Date(`${localYear}-${localMonth}-${localDay}T${localHours}:${localMinutes}:${localSeconds}.000Z`);

        console.log('[TrainerSessions] Time comparison:', { pseudoUtcNow: pseudoUtcNow.toISOString(), startTime: startTime.toISOString(), isBefore: pseudoUtcNow < startTime });

        // Check if current time is before scheduled start time
        if (pseudoUtcNow < startTime) {
          const diffMs = startTime - pseudoUtcNow;
          const diffMins = Math.ceil(diffMs / 60000);
          const hours = Math.floor(diffMins / 60);
          const mins = diffMins % 60;

          let timeRemaining = '';
          if (hours > 0) {
            timeRemaining = `${hours} hour${hours > 1 ? 's' : ''} ${mins > 0 ? `${mins} minute${mins > 1 ? 's' : ''}` : ''}`;
          } else {
            timeRemaining = `${mins} minute${mins > 1 ? 's' : ''}`;
          }

          setEarlyStartModal({
            show: true,
            timeRemaining: timeRemaining.trim(),
            scheduledTime: formatTime(startTime)
          });
          return;
        }
      }
    }

    try {
      const response = await trainerService.launchSession(sessionId);
      if (response.success) {
        notifications.success('Session started successfully!');
        // Navigate to session control page
        navigate(`/trainer/session/${sessionId}/control`);
      } else {
        notifications.error(response.error || 'Failed to start session');
      }
    } catch (error) {
      console.error('Error starting session:', error);
      notifications.error(error.message || 'Failed to start session. Please try again.');
    }
  };


  const handleResumeSession = async (sessionId, e) => {
    e.stopPropagation();
    try {
      const response = await trainerService.resumeSession(sessionId);
      if (response.success) {
        notifications.success('Session resumed successfully!');
        // Navigate to control page
        navigate(`/trainer/session/${sessionId}/control`);
      } else {
        notifications.error(response.error || 'Failed to resume session');
      }
    } catch (error) {
      console.error('Error resuming session:', error);
      notifications.error(error.message || 'Failed to resume session');
    }
  };

  // Filter sessions locally for display
  const filteredSessions = sessions.filter((session) => {
    // Apply status filter
    if (filter !== 'all' && session.status !== filter) return false;
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const nameMatch = session.name?.toLowerCase().includes(query);
      const codeMatch = session.code?.toLowerCase().includes(query);
      if (!nameMatch && !codeMatch) return false;
    }
    return true;
  });

  // Pagination
  const totalPages = Math.ceil(filteredSessions.length / itemsPerPage);
  const paginatedSessions = filteredSessions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to first page when filter/search/itemsPerPage changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchQuery, itemsPerPage]);

  return (
    <DashboardLayout title="My Sessions">
      <div className="p-4 space-y-4">
        {/* Back Button */}
        <button
          onClick={() => navigate('/trainer/dashboard')}
          className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 transition-colors group"
        >
          <svg className="w-4 h-4 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm font-medium">Back to Dashboard</span>
        </button>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-secondary-900">My Sessions</h1>
            <p className="text-sm text-neutral-600 mt-1">{soloTrainer ? 'Manage your training sessions' : 'Sessions assigned to you'}</p>
          </div>
          {soloTrainer && (
            <Button
              variant="primary-filled"
              onClick={() => navigate('/trainer/sessions/new')}
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
              }
            >
              Create Session
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
          <StatCard label="Total"     value={stats.total}     icon={LayoutGrid}   variant="slate"   onClick={() => setFilter('all')}       active={filter === 'all'} />
          <StatCard label="Live"      value={stats.live}      icon={PlayCircle}   variant="emerald" onClick={() => setFilter('live')}      active={filter === 'live'} />
          <StatCard label="Scheduled" value={stats.scheduled} icon={Calendar}     variant="blue"    onClick={() => setFilter('scheduled')} active={filter === 'scheduled'} />
          <StatCard label="Completed" value={stats.completed} icon={CheckCircle2} variant="violet"  onClick={() => setFilter('completed')} active={filter === 'completed'} />
          <StatCard label="Expired"   value={stats.expired}   icon={Clock}        variant="rose"    onClick={() => setFilter('expired')}   active={filter === 'expired'} />
        </div>

        {/* Sessions Table */}
        <div className="bg-white rounded-2xl border border-neutral-100 overflow-hidden">
          {/* Search and Filter inside card */}
          <div className="p-5 border-b border-neutral-100">
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div className="relative flex-1 w-full sm:max-w-md">
                <svg
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search by session name or code..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-10 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="flex-1 sm:flex-none px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white appearance-none pr-8 cursor-pointer"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.25em 1.25em' }}
                >
                  <option value="all">All Status</option>
                  <option value="live">Live</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="completed">Completed</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
            </div>
          </div>

          <ResponsiveDataTable
            columns={[
              {
                key: 'session',
                label: 'Session',
                card: 'header',
                render: (s) => (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-50 rounded-full flex items-center justify-center text-primary-600 font-semibold text-sm flex-shrink-0">
                      {s.name?.charAt(0) || 'S'}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-neutral-900">{s.name}</p>
                      <p className="text-xs text-neutral-400">Code: {s.code}</p>
                    </div>
                  </div>
                ),
              },
              {
                key: 'organization',
                label: 'Organization',
                card: 'body',
                render: (s) => <span className="text-sm text-neutral-700">{s.organization?.name || 'N/A'}</span>,
              },
              {
                key: 'datetime',
                label: 'Date & Time',
                card: 'body',
                render: (s) => (
                  <div>
                    <p className="text-sm text-neutral-900">{s.startTime ? formatDateWithTimezone(s.startTime) : 'Not set'}</p>
                    <p className="text-xs text-neutral-400">{formatTime(s.startTime)} - {formatTime(s.endTime)}</p>
                  </div>
                ),
              },
              {
                key: 'participants',
                label: 'Participants',
                card: 'body',
                render: (s) => (
                  <span className="text-sm text-neutral-700">
                    {s.currentParticipants || s.participants?.length || 0}/{s.expectedParticipants || s.maxParticipants}
                  </span>
                ),
              },
              {
                key: 'status',
                label: 'Status',
                card: 'body',
                render: (s) => <Badge variant={s.status} dot={s.status === 'live'}>{s.status}</Badge>,
              },
              {
                key: 'actions',
                label: '',
                card: 'footer',
                align: 'right',
                stopPropagation: true,
                render: (s) => (
                  <div className="flex items-center justify-end gap-1">
                    {s.status === 'live' && (
                      <button onClick={() => navigate(`/trainer/session/${s._id}/control`)} className="p-2 rounded-lg text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 transition-colors" title="Control">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                      </button>
                    )}
                    {s.status === 'scheduled' && (
                      <>
                        <button onClick={() => handleStartSession(s._id)} className="p-2 rounded-lg text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 transition-colors" title="Start Session">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                        </button>
                        {/* Solo trainers own their sessions outright, so they may edit
                            the details themselves. Org-employed trainers may not -
                            that is the client's call, same rule as reschedule. */}
                        {soloTrainer && (
                          <button onClick={(e) => { e.stopPropagation(); navigate(`/trainer/sessions/${s._id}/edit`); }} className="p-2 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors" title="Edit">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                          </button>
                        )}
                        <button onClick={() => navigate(`/trainer/sessions/${s._id}/setup`)} className="p-2 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors" title="Setup">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                        </button>
                        <button onClick={() => navigate(`/trainer/sessions/${s._id}`)} className="p-2 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors" title="View">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        </button>
                      </>
                    )}
                    {/* Report is available at every status, matching the client
                        and admin lists; a scheduled session shows pre-session data. */}
                      <button onClick={() => navigate(`/trainer/sessions/${s._id}/report`)} className="p-2 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors" title="View Report">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                      </button>
                    {s.status === 'paused' && (
                      <button onClick={(e) => handleResumeSession(s._id, e)} className="p-2 rounded-lg text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 transition-colors" title="Resume">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                      </button>
                    )}
                    {(s.status === 'expired') && (
                      <>
                        {/* Reschedule is only available to solo trainers
                            (individual orgs that own their own sessions).
                            Org-employed trainers don't have permission to
                            reschedule — that's the client's call. */}
                        {soloTrainer && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setRescheduleModal({ isOpen: true, session: s }); }}
                            className="p-2 rounded-lg text-primary-600 hover:text-primary-700 hover:bg-primary-50 transition-colors ring-1 ring-inset ring-primary-200/70"
                            title="Reschedule"
                          >
                            <CalendarClock size={16} strokeWidth={1.75} />
                          </button>
                        )}
                        <button onClick={() => navigate(`/trainer/sessions/${s._id}`)} className="p-2 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors" title="View">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        </button>
                      </>
                    )}
                    {soloTrainer && s.status !== 'live' && (
                      <>
                        <button onClick={(e) => handleDuplicateSession(s, e)} className="p-2 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors" title="Duplicate">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                        </button>
                        <button onClick={(e) => handleDeleteSession(s, e)} className="p-2 rounded-lg text-neutral-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Delete">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </>
                    )}
                  </div>
                ),
              },
            ]}
            data={paginatedSessions}
            keyExtractor={(s) => s._id}
            onRowClick={(s) => navigate(`/trainer/sessions/${s._id}`)}
            emptyState={
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-neutral-100 rounded-full mb-4">
                  <svg className="w-6 h-6 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-sm text-neutral-600 font-medium mb-1">
                  {filter === 'all' ? 'No sessions assigned yet' : `No ${filter} sessions`}
                </p>
                <p className="text-xs text-neutral-400">Sessions will appear here when a client assigns you</p>
              </div>
            }
          />

          {/* Pagination */}
          {filteredSessions.length > 0 && (
            <div className="px-3 sm:px-5 py-3 sm:py-3.5 border-t border-neutral-100">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                totalItems={filteredSessions.length}
              />
            </div>
          )}
        </div>

        {/* Quick Tips Card */}
        <Card padding="sm" className="bg-blue-50 border-blue-200">
          <div className="flex items-start gap-2">
            <svg className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <p className="text-xs font-semibold text-blue-900 mb-1">Trainer Responsibilities</p>
              <ul className="text-xs text-blue-800 space-y-0.5">
                <li>• Create sub-sessions and activities before the session starts</li>
                <li>• Review participant challenges and personalization responses</li>
                <li>• Start the session at the scheduled time</li>
                <li>• Monitor participant engagement during live sessions</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>

      {/* Early Start Warning Modal */}
      <Modal
        isOpen={earlyStartModal.show}
        onClose={() => setEarlyStartModal({ show: false, timeRemaining: '', scheduledTime: '' })}
        title="Session Not Yet Scheduled"
        size="sm"
      >
        <div className="text-center py-4">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">Too Early to Start</h3>
          <p className="text-neutral-600 mb-4">
            This session is scheduled to start at <span className="font-semibold text-primary-600">{earlyStartModal.scheduledTime}</span>.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-amber-800">
              <span className="font-semibold">{earlyStartModal.timeRemaining}</span> remaining until the session can be started.
            </p>
          </div>
          <p className="text-sm text-neutral-500">
            Please wait until the scheduled start time to begin the session.
          </p>
          <div className="mt-6">
            <Button
              variant="primary"
              onClick={() => setEarlyStartModal({ show: false, timeRemaining: '', scheduledTime: '' })}
              className="w-full"
            >
              Got it
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete confirmation — replaces the native window.confirm() with the
          shared ConfirmDialog so the prompt matches the rest of the app. */}
      <ConfirmDialog
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, session: null })}
        onConfirm={confirmDeleteSession}
        title="Delete this session?"
        message={
          deleteModal.session
            ? `"${deleteModal.session.name}" will be permanently deleted along with its data. This action cannot be undone.`
            : ''
        }
        confirmText="Delete Session"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Reschedule modal — opens for expired sessions. On success the page
          refetches so the row moves out of the Expired bucket and into
          Scheduled with the new date. */}
      <RescheduleSessionModal
        isOpen={rescheduleModal.isOpen}
        onClose={() => setRescheduleModal({ isOpen: false, session: null })}
        session={rescheduleModal.session}
        onSuccess={() => fetchSessions()}
      />
    </DashboardLayout>
  );
};

export default TrainerSessionsPage;
